import { RefreshCw, Ship } from 'lucide-react';
import { cn } from '@my/ui';
import type { FerryBooking } from '@my/ferry-api';
import { formatPortTime, utcToZoned } from '../portTime';
import { ferryCarrierStatusLabel, ferryRowStatus, type FerryRowStatus } from '../status';

const TONE: Record<FerryRowStatus, string> = {
  Confirmed: 'bg-green-50 text-green-700 ring-green-200',
  Booked: 'bg-sky-50 text-sky-700 ring-sky-200',
  Alert: 'bg-red-50 text-red-700 ring-red-200',
};

/** The carrier's view of a booked crossing, read from My.Ferry: number, status, ship, and the reason when it went wrong. */
export function FerryStatusStrip({
  ferryBooking,
  carrierName,
  originZone,
  onRefresh,
  refreshing,
}: {
  ferryBooking: FerryBooking;
  carrierName: string;
  originZone: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const status = ferryRowStatus(ferryBooking);
  const departs = ferryBooking.scheduledDeparture
    ? utcToZoned(ferryBooking.scheduledDeparture, originZone)
    : null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
      <Ship className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
      <span className="font-semibold text-slate-700">{carrierName}</span>
      {ferryBooking.carrierBookingNumber != null && (
        <span className="font-mono text-slate-700">#{ferryBooking.carrierBookingNumber}</span>
      )}
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset',
          TONE[status],
        )}
      >
        {status === 'Alert' && ferryBooking.integrationStatus === 'Failed'
          ? 'Refused'
          : ferryCarrierStatusLabel(ferryBooking.carrierStatus)}
      </span>
      {ferryBooking.shipName && <span className="text-slate-600">{ferryBooking.shipName}</span>}
      {departs && (
        <span className="tabular-nums text-slate-600">
          {departs.date} {formatPortTime(ferryBooking.scheduledDeparture, originZone)}
        </span>
      )}
      {status === 'Alert' && ferryBooking.message && (
        <span className="basis-full text-red-700">{ferryBooking.message}</span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title={`Re-read the booking from ${carrierName}`}
        aria-label="Refresh from carrier"
        className="ml-auto flex h-6 w-6 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
      >
        <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
      </button>
    </div>
  );
}
